import { getValue, getLineCount } from './value'
import {
    bucketedMediaHeight,
    RESERVED_DIMLESS_MEDIA_HEIGHT,
    resolveRecordingFrameHeight,
    isAudioRecording,
} from './mediaBuckets'
import { getContentEmbedLinks } from './contentEmbedLinks'
import { getDisplayUrlSelection } from './urlAnswerSelection'
import { isTweetUrl, projectTweetCardGeometry } from './tweetCardHeight'
import {
    extractLeaderboardEmbedConfig,
    extractTdpEmbedConfig,
    getUrlModelYoutubeVideoId,
} from 'components/shared/linkPreviewUrls'
import { getAnswerDisplayText } from 'components/answers/answerDisplayText'
import { areSamePreviewUrl } from 'components/answers/payloadRemoval'
import {
    answerImageInputClassificationItem,
    answerRecordingInputClassificationItem,
    aiInputDescriptorsForAnswer,
    aiRunOutputIsConsumedInput,
    fileInputClassificationItem,
    mediaEntryIsAiInput,
} from './aiInputMediaClassification'
import {
    classifyFileEmbedMedia,
    tallFileEmbedKind,
} from 'components/answers/FileEmbed/fileEmbedMediaClassification'
import { payloadMediaKind } from 'components/answers/Answer/payloadMediaKind'
import { firstValidMediaTimestamp, sortMediaByCreation } from './mediaDisplayOrder'
import { getDeltaSummary } from 'components/answers/largeDelta'
import { actionProducesGeneratedOutput } from 'utils/actionOutputPolicy'
import { combinedMediaItemReady } from './combinedMedia'

const MAX_PROJECTED_MEDIA_ITEMS = 4
const projectionMemo = new WeakMap()
const embeddedProjectionMemo = new WeakMap()

const hasArrayItems = value => Array.isArray(value) && value.length > 0

const positiveDimension = value => {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? number : null
}

const bucketedTileHeight = (width, height) => {
    const resolvedWidth = positiveDimension(width)
    const resolvedHeight = positiveDimension(height)
    return resolvedWidth && resolvedHeight
        ? bucketedMediaHeight(resolvedWidth, resolvedHeight)
        : RESERVED_DIMLESS_MEDIA_HEIGHT
}

// height/width ratio, or null when dims are unknown. Projections are retained across resizes,
// so they carry width-independent ratios; estimators clamp tile heights at estimate time with
// the current mobile payload width (see estimateAnswerPayloadHeight).
const mediaTileRatio = (width, height) => {
    const resolvedWidth = positiveDimension(width)
    const resolvedHeight = positiveDimension(height)
    return resolvedWidth && resolvedHeight ? resolvedHeight / resolvedWidth : null
}

const outputLooksVisual = output => ['image', 'video'].includes(payloadMediaKind(output))

const outputLooksAudio = output => payloadMediaKind(output) === 'audio'

const outputUrl = output =>
    output?.url ||
    output?.preview_url ||
    output?.previewUrl ||
    output?.thumbnailUrl ||
    output?.urls?.original ||
    output?.urls?.large ||
    output?.urls?.medium ||
    output?.urls?.small ||
    null

const outputLooksLikeStreamVisualMedia = output => {
    const contentType = String(output?.mime || output?.content_type || output?.contentType || '')
        .trim()
        .toLowerCase()
    const kind = String(output?.type || output?.kind || '')
        .trim()
        .toLowerCase()

    if (kind === 'audio' || contentType.startsWith('audio/')) return false
    if (kind === 'file' || kind === 'run') return false
    if (kind === 'image' || kind === 'video') return true
    if (contentType.startsWith('image/') || contentType.startsWith('video/')) return true

    const url = String(outputUrl(output) || '')
        .split(/[?#]/)[0]
        .toLowerCase()
    if (!url) return false
    if (/\.(mp3|wav|ogg|oga|opus|flac|m4a|aac|aiff?|caf|amr)$/.test(url)) return false
    if (/\.(gif|jpe?g|png|webp|avif|heic|heif|mp4|mov|m4v|webm)$/.test(url)) return true
    if (/\.(pdf|zip|txt|csv|docx?|xlsx?|pptx?)$/.test(url)) return false

    return false
}

const aiOutputEntries = answer =>
    (Array.isArray(answer?.ai_runs) ? answer.ai_runs : []).flatMap(run =>
        (Array.isArray(run?.output_urls) ? run.output_urls : []).map(output => ({
            output,
            run,
            created_at: firstValidMediaTimestamp(
                output?.created_at,
                output?.createdAt,
                run?.created_at,
                run?.createdAt,
            ),
            attached_at: firstValidMediaTimestamp(output?.attached_at, output?.attachedAt),
            blob_created_at: firstValidMediaTimestamp(
                output?.blob_created_at,
                output?.blobCreatedAt,
            ),
        })),
    )

const answerImageEntries = answer =>
    hasArrayItems(answer?.images_v2)
        ? sortMediaByCreation(answer.images_v2)
        : hasArrayItems(answer?.image_urls)
        ? sortMediaByCreation(answer.image_urls)
        : []

const textProjection = answer => {
    try {
        const value = getValue(answer)
        const explicitLineCount = getLineCount(value) || 0
        const text = getAnswerDisplayText(answer)
        const deltaSummary = getDeltaSummary(value)
        return {
            text: String(text),
            explicitLineCount,
            contentCharCount: Number(answer?.content_char_count || 0),
            hasPostAnswerContent:
                typeof answer?.post_answer_has_content === 'boolean'
                    ? answer.post_answer_has_content
                    : deltaSummary.hasText || deltaSummary.hasImage,
        }
    } catch {
        return {
            text: '',
            explicitLineCount: 1,
            contentCharCount: Number(answer?.content_char_count || 0),
            hasPostAnswerContent:
                typeof answer?.post_answer_has_content === 'boolean'
                    ? answer.post_answer_has_content
                    : false,
        }
    }
}

const mediaProjection = (answer, aiInputDescriptors) => {
    // AI-action input media (attached images/outputs the answer's actions consumed) renders as
    // the fixed-height thumbnail strip, not payload tiles (Answer applyAiActionInputLabels /
    // AiInputMediaStrip). Classify with the same shared matcher and drop those entries from the
    // tile lists so the reserve matches the render; the strip itself is charged as
    // BUCKETS_AI_INPUT_STRIP_HEIGHT in answerPlaceholderHeight. Fingerprint stubs persist the
    // classification as `ai_input: 1` flags (answerHeightFingerprint) because fingerprinted
    // ai_runs carry no action_payload to re-match against.
    const allImageEntries = answerImageEntries(answer)
    const imageEntryIsAiInput = image => mediaEntryIsAiInput(image, aiInputDescriptors)
    const imageEntries = allImageEntries.filter(image => !imageEntryIsAiInput(image))
    const aiInputImageCount = allImageEntries.length - imageEntries.length
    const projectedImageEntries = imageEntries.slice(0, MAX_PROJECTED_MEDIA_ITEMS)
    const imageTileHeights = projectedImageEntries.map(image =>
        bucketedTileHeight(image?.image_width, image?.image_height),
    )
    const imageTileRatios = projectedImageEntries.map(image =>
        mediaTileRatio(image?.image_width, image?.image_height),
    )
    const fallbackImageItem = answerImageInputClassificationItem(answer)
    const fallbackImageIsAiInput =
        allImageEntries.length === 0 &&
        !!fallbackImageItem.url &&
        mediaEntryIsAiInput(fallbackImageItem, aiInputDescriptors)
    // Answer's legacy fallback is a payload item too, so an input fallback moves into the strip
    // and must not retain the single-image tile reserve.
    const hasSingleImage =
        allImageEntries.length === 0 && !!fallbackImageItem.url && !fallbackImageIsAiInput
    const singleImageHeight = hasSingleImage
        ? bucketedTileHeight(answer?.image_width, answer?.image_height)
        : 0
    const singleImageRatio = hasSingleImage
        ? mediaTileRatio(answer?.image_width, answer?.image_height)
        : null

    const allAiOutputEntries = aiOutputEntries(answer)
    const consumedAiOutputEntries = allAiOutputEntries.filter(
        entry =>
            (!!outputUrl(entry.output) || entry.output?.ai_input === 1) &&
            aiRunOutputIsConsumedInput(entry.run, entry.output, aiInputDescriptors),
    )
    const strictAiOutputEntries = allAiOutputEntries.filter(
        entry => outputLooksVisual(entry.output) && !consumedAiOutputEntries.includes(entry),
    )
    const aiOutputReadyForCombinedMedia = entry =>
        entry.output?.media_ready === 1 ||
        combinedMediaItemReady({
            url: outputUrl(entry.output),
            status: entry.run?.status,
        })
    const strictAiOutputs = strictAiOutputEntries.map(entry => entry.output)
    const projectedAiOutputs = strictAiOutputs.slice(0, MAX_PROJECTED_MEDIA_ITEMS)
    const aiTileHeights = projectedAiOutputs.map(output =>
        bucketedTileHeight(output?.width, output?.height),
    )
    const aiTileRatios = projectedAiOutputs.map(output =>
        mediaTileRatio(output?.width, output?.height),
    )
    const aiTileKinds = projectedAiOutputs.map(output => payloadMediaKind(output))
    // Answer renders AI outputs and attached images from one chronological list, capped at four,
    // before PayloadContainer groups those four by media kind. Preserve that exact selected set;
    // separate per-source caps can drop an earlier generated video when four images also exist.
    const visualTiles = sortMediaByCreation([
        ...strictAiOutputEntries.map(entry => ({
            created_at: entry.created_at,
            attached_at: entry.attached_at,
            blob_created_at: entry.blob_created_at,
            height: bucketedTileHeight(entry.output?.width, entry.output?.height),
            ratio: mediaTileRatio(entry.output?.width, entry.output?.height),
            kind: payloadMediaKind(entry.output),
            attributed: true,
            ready: aiOutputReadyForCombinedMedia(entry),
        })),
        ...imageEntries.map(image => ({
            created_at: image?.created_at || image?.createdAt || null,
            attached_at: image?.attached_at || image?.attachedAt || null,
            blob_created_at: image?.blob_created_at || image?.blobCreatedAt || null,
            height: bucketedTileHeight(image?.image_width, image?.image_height),
            ratio: mediaTileRatio(image?.image_width, image?.image_height),
            kind: 'image',
            attributed: false,
            ready: true,
        })),
    ]).slice(0, MAX_PROJECTED_MEDIA_ITEMS)
    const aiAudioOutputCount = allAiOutputEntries.filter(
        entry => outputLooksAudio(entry.output) && !consumedAiOutputEntries.includes(entry),
    ).length
    const aiReadyAudioOutputCount = allAiOutputEntries.filter(
        entry =>
            outputLooksAudio(entry.output) &&
            !consumedAiOutputEntries.includes(entry) &&
            aiOutputReadyForCombinedMedia(entry),
    ).length
    // Non-visual/audio outputs and status-only runs are still PayloadContainer items. They
    // prevent an image+audio pair from being the entire rendered payload even though their own
    // height terms are projected separately.
    const aiOtherOutputCount = allAiOutputEntries.filter(
        entry =>
            !consumedAiOutputEntries.includes(entry) &&
            !outputLooksVisual(entry.output) &&
            !outputLooksAudio(entry.output),
    ).length
    const runGenerationKey = run =>
        [
            run?.action_index ?? run?.actionIndex ?? 0,
            run?.generation_index ?? run?.generationIndex ?? 0,
        ].join(':')
    const completedGenerationKeys = new Set(
        (Array.isArray(answer?.ai_runs) ? answer.ai_runs : [])
            .filter(run => Array.isArray(run?.output_urls) && run.output_urls.length > 0)
            .map(runGenerationKey),
    )
    const aiStatusOutputCount = (Array.isArray(answer?.ai_runs) ? answer.ai_runs : []).filter(
        run => {
            const actionTag = String(run?.action_tag || run?.actionTag || '').replace(/^!+/, '')
            if (!actionProducesGeneratedOutput(actionTag)) return false
            if (Array.isArray(run?.output_urls) && run.output_urls.length > 0) return false
            return (
                !completedGenerationKeys.has(runGenerationKey(run)) ||
                run?.status === 'failed' ||
                run?.status === 'canceled'
            )
        },
    ).length

    const recordingItem = answerRecordingInputClassificationItem(answer)
    const recordingIsAiInput =
        !!recordingItem.url && mediaEntryIsAiInput(recordingItem, aiInputDescriptors)
    const hasRecording = !!recordingItem.url && !recordingIsAiInput
    const recordingHeight = hasRecording ? resolveRecordingFrameHeight(answer) : 0
    const recordingRatio =
        hasRecording && !isAudioRecording(answer)
            ? mediaTileRatio(answer?.video_width, answer?.video_height)
            : null

    const fileInputCount = (Array.isArray(answer?.file_urls) ? answer.file_urls : []).filter(
        file =>
            (!!file?.url || file?.ai_input === 1) &&
            mediaEntryIsAiInput(fileInputClassificationItem(file), aiInputDescriptors),
    ).length
    const aiInputMediaCount = Math.min(
        6,
        aiInputImageCount +
            (fallbackImageIsAiInput ? 1 : 0) +
            (recordingIsAiInput ? 1 : 0) +
            fileInputCount +
            consumedAiOutputEntries.length,
    )
    const streamAiOutputs = allAiOutputEntries
        .filter(entry => !consumedAiOutputEntries.includes(entry))
        .map(entry => entry.output)
        .filter(outputLooksLikeStreamVisualMedia)
    const legacyImageCount = imageEntries.length + (hasSingleImage ? 1 : 0)
    const recordingCount = hasRecording ? 1 : 0
    const firstImageEntry = imageEntries[0]
    const firstVisualMedia = hasSingleImage
        ? { width: answer?.image_width, height: answer?.image_height }
        : firstImageEntry
        ? { width: firstImageEntry?.image_width, height: firstImageEntry?.image_height }
        : hasRecording && !isAudioRecording(answer)
        ? { width: answer?.video_width, height: answer?.video_height }
        : streamAiOutputs[0]

    return {
        hasAnswerMedia: !!(
            answer?.answer_image_url ||
            answer?.local_answer_image_url ||
            answer?.recording_url ||
            answer?.mp4_recording_url ||
            answer?.recording_thumbnail_url ||
            hasArrayItems(answer?.images_v2) ||
            hasArrayItems(answer?.image_urls)
        ),
        hasRecording,
        imageTileHeights,
        imageTileRatios,
        hasSingleImage,
        singleImageHeight,
        singleImageRatio,
        aiTileHeights,
        aiTileRatios,
        aiTileKinds,
        visualTiles,
        aiInputMediaCount,
        aiVisualOutputCount: strictAiOutputs.length,
        aiAudioOutputCount,
        aiReadyAudioOutputCount,
        aiOtherOutputCount,
        aiStatusOutputCount,
        recordingHeight,
        recordingRatio,
        streamAiVisualOutputCount: streamAiOutputs.length,
        streamVisualMediaCount: legacyImageCount + recordingCount + streamAiOutputs.length,
        streamFirstVisualMedia: {
            width: positiveDimension(firstVisualMedia?.width),
            height: positiveDimension(firstVisualMedia?.height),
        },
    }
}

const contentEmbedProjection = answer => {
    const links = getContentEmbedLinks(answer)
    const attachedUrlAddress = answer?.url?.address || null
    const renderedTextPreviews =
        !attachedUrlAddress &&
        links.twitter.length > 0 &&
        !answer?.answer_image_url &&
        !links.streamInvite
            ? links.twitter
                  .filter(href => {
                      if (areSamePreviewUrl(attachedUrlAddress, href)) return false
                      if (extractLeaderboardEmbedConfig(href)) return false
                      if (extractTdpEmbedConfig(href)) return false
                      return true
                  })
                  .slice(0, 1).length
            : 0

    return {
        links,
        streamCount: links.stream.length,
        leaderboardCount: links.leaderboard.length,
        tdpCount: links.tdp.length,
        marketCount: links.market.length,
        renderedTextPreviewCount: renderedTextPreviews,
        streamInvite: links.streamInvite,
    }
}

const urlProjection = answer => {
    const url = answer?.url
    if (!url || typeof url !== 'object') return null

    // Mirror the render gate: only real video URLs project as youtube, even when
    // a stale cached answer carries a bogus video_id (e.g. "playlist").
    const youtubeVideoId = getUrlModelYoutubeVideoId(url)
    const kind = isTweetUrl(url) ? 'tweet' : youtubeVideoId ? 'youtube' : 'generic'
    const address = url.address || ''
    const previewDescription = url.service_json?.description || ''
    const visibleSelectionText =
        typeof url.visible_selection === 'string'
            ? url.visible_selection
            : getDisplayUrlSelection({
                  answer,
                  address,
                  previewDescription,
                  isYoutube: kind === 'youtube',
              })

    return {
        present: true,
        kind,
        address,
        videoId: youtubeVideoId,
        hasServiceJson: !!url.service_json,
        visibleSelectionText,
        tweet: kind === 'tweet' ? projectTweetCardGeometry(url) : null,
    }
}

const embedLooksLikeAnswer = embed =>
    !!(
        embed?.delta_json ||
        embed?.content ||
        embed?.display_content ||
        hasArrayItems(embed?.images_v2) ||
        hasArrayItems(embed?.image_urls)
    )

const embeddedProjection = embed => ({
    looksLikeAnswer: embedLooksLikeAnswer(embed),
    projection: resolveEmbeddedAnswerGeometryProjection(embed),
})

const fileProjection = (answer, aiInputDescriptors) =>
    hasArrayItems(answer?.file_urls)
        ? answer.file_urls
              .filter(
                  file =>
                      !mediaEntryIsAiInput(fileInputClassificationItem(file), aiInputDescriptors),
              )
              .slice(0, 3)
              .map(file => ({
                  tall: !!tallFileEmbedKind(file),
                  audio: classifyFileEmbedMedia(file).isAudio,
              }))
        : []

function buildAnswerGeometryProjection(answer, { includeEmbeds = true } = {}) {
    if (!answer || typeof answer !== 'object') return null

    const text = textProjection(answer)
    const aiInputDescriptors = aiInputDescriptorsForAnswer(answer)
    const media = mediaProjection(answer, aiInputDescriptors)
    const contentEmbeds = contentEmbedProjection(answer)
    const embeds =
        includeEmbeds && hasArrayItems(answer?.embeds)
            ? answer.embeds.slice(0, MAX_PROJECTED_MEDIA_ITEMS).map(embeddedProjection)
            : []

    return {
        id: answer.id,
        text: text.text,
        explicitLineCount: text.explicitLineCount,
        contentCharCount: text.contentCharCount,
        hasPostAnswerContent: text.hasPostAnswerContent,
        media,
        url: urlProjection(answer),
        embeds,
        contentEmbeds,
        fileRows: fileProjection(answer, aiInputDescriptors),
        hasPreview: !!(answer?.url || hasArrayItems(answer?.embeds)),
        pollOptionCount:
            answer?.message_type === 'poll' && hasArrayItems(answer?.poll_options_payload)
                ? answer.poll_options_payload.length
                : 0,
    }
}

export const primeAnswerGeometryProjection = (answer, options = {}) => {
    if (!answer || typeof answer !== 'object') return null

    const retained = answer.geometry_projection
    if (retained && typeof retained === 'object') return retained

    const memoized = projectionMemo.get(answer)
    if (memoized) return memoized

    const projection = buildAnswerGeometryProjection(answer, options)
    projectionMemo.set(answer, projection)
    return projection
}

export const adoptAnswerGeometryProjection = (answer, projection) => {
    if (!answer || typeof answer !== 'object' || !projection) return projection || null

    projectionMemo.set(answer, projection)
    return projection
}

export const adoptEmbeddedAnswerGeometryProjection = (answer, projection) => {
    if (!answer || typeof answer !== 'object' || !projection) return projection || null

    const embeddedProjection = projection.embeds?.length
        ? { ...projection, embeds: [] }
        : projection
    embeddedProjectionMemo.set(answer, embeddedProjection)
    return embeddedProjection
}

export const resolveEmbeddedAnswerGeometryProjection = answer => {
    if (!answer || typeof answer !== 'object') return null

    const memoized = embeddedProjectionMemo.get(answer)
    if (memoized) return memoized

    const retained = answer.geometry_projection
    const projection =
        retained && typeof retained === 'object'
            ? { ...retained, embeds: [] }
            : buildAnswerGeometryProjection(answer, { includeEmbeds: false })
    embeddedProjectionMemo.set(answer, projection)
    return projection
}

export const resolveAnswerGeometryProjection = answer => {
    return primeAnswerGeometryProjection(answer)
}
